/**
 * 权限点判定（spec 02 §5、08 §6）。
 *
 * **前端权限只管体验，不是安全边界。** 服务端必须独立校验（spec 00 §3.2-9、10）。
 * 这里的作用只有两个：无权限的菜单项不渲染（Skill shell.md 要求不灰置）、
 * 无权限的主操作按钮不渲染。绕过它拿不到任何数据。
 *
 * 能力名就是服务端权限点（tenant.Permission 字面值），前端没有自己的一层名字。
 * 真源是服务端：`/auth/me` 返回的 `permissions` 一到，判定就完全按它走；
 * 兜底用的角色 × 权限矩阵是后端生成的（role-perms.generated.ts，`make gen-perms`），
 * 只在 mock 模式与契约被破坏时用。曾经前端自己手抄一套矩阵，
 * 结果财务看得到「提交认证」、成员看得到「雇佣」，点进去全是 403，
 * 于是有人想办法绕——邀请自己当管理员，把企业主给盖掉了（2026-09-02）。
 *
 * 角色标识按 spec 00 §4 术语表用 tenant_*（术语表明确禁用 corp）。
 */
import { ROLE_MATRIX } from './role-perms.generated';

export type Role = (typeof ROLE_MATRIX.roles)[number]['code'];

export type Capability = (typeof ROLE_MATRIX.permissions)[number]['code'];

export const ROLES: readonly Role[] = ROLE_MATRIX.roles.map((r) => r.code);

const ROLE_PERMS = new Map<string, ReadonlySet<string>>(
  ROLE_MATRIX.roles.map((r) => [r.code, new Set<string>(r.permissions)]),
);

let serverPermissions: ReadonlySet<string> | null = null;

/**
 * 用服务端下发的权限点覆盖本地矩阵。空数组视为「没给」，退回矩阵——
 * 每个真实角色至少有 tenant:read，空集只可能来自 mock 或契约被破坏。
 */
export function syncServerPermissions(perms: readonly string[] | null | undefined): void {
  serverPermissions = perms && perms.length > 0 ? new Set(perms) : null;
}

/**
 * 判定某组角色是否具备某个权限点。
 * 未知角色（后端新增了我们还不认识的角色）一律**无权限**——这是安全侧的默认拒绝，
 * 与「未知枚举安全降级为展示原码」是两回事，不能混。
 */
export function can(roles: readonly string[] | undefined, cap: Capability): boolean {
  if (serverPermissions) return serverPermissions.has(cap);
  if (!roles || roles.length === 0) return false;
  return roles.some((r) => ROLE_PERMS.get(r)?.has(cap) ?? false);
}

/** 任一能力命中即可。用于「这一组菜单要不要出现」。 */
export function canAny(roles: readonly string[] | undefined, caps: Capability[]): boolean {
  return caps.some((c) => can(roles, c));
}
